<template>
    <div>
        <div
            v-if="groupDetails && groupDetails.touristsQuantity"
            class="d-flex text-caption align-center full-width mb-2 text-black"
        >
            <span class="font-weight-medium me-1" v-text="`${$t('tourists')}:`" />
            <span>
                {{ groupDetails.touristsQuantity }}
            </span>
            <v-spacer />
            <v-btn color="primary" icon="mdi-file-excel" variant="text" @click="downloadExcel">
                <v-icon>mdi-file-excel</v-icon>
            </v-btn>
        </div>
        <v-expand-transition>
            <v-data-table
                v-show="groupDetails"
                :headers="headers"
                :items="guestsPrepared"
                :items-per-page="guestsPrepared && guestsPrepared.length"
                hide-default-footer
                :mobile-breakpoint="600"
                class="elevation-1"
                :class="isShowSeats && 'table-with-seat'"
            >
                <template v-slot:item.name="{item}">
                    <div class="d-flex align-center" style="min-width: max-content">
                        <div class="d-flex flex-column flex-grow-1">
                            <div class="d-flex align-center pr-4" style="min-height: 55px">
                                <v-slide-x-transition>
                                    <v-checkbox
                                        v-if="
                                            isExcursion &&
                                            (!selectedGuests[0] || selectedGuests[0].orderId === item.orderId)
                                        "
                                        v-model="selectedGuests"
                                        density="compact"
                                        class="me-2 mt-n1 flex-shrink-0"
                                        hide-details
                                        :value="item"
                                    />
                                </v-slide-x-transition>
                                <div class="d-flex flex-column py-3 flex-shrink-0">
                                    <span class="font-weight-medium text-no-wrap">
                                        {{ `${item.firstName} ${item.lastName}` }}
                                    </span>
                                    <span
                                        v-if="item.departure.pickUpSign"
                                        style="white-space: nowrap"
                                        class="text-caption text--secondary font-weight-medium"
                                    >
                                        {{ `${$t('transfers.pickup_sign')}: ${item.departure.pickUpSign}` }}
                                    </span>
                                    <span
                                        v-if="!item.adult"
                                        style="white-space: nowrap"
                                        class="text-caption text--secondary"
                                    >
                                        {{ `${$t('age')}: ${item.age}` }}
                                    </span>
                                </div>
                            </div>
                            <div
                                v-for="guest in item.extraGuests"
                                :key="guest.id"
                                class="d-flex align-center pr-4"
                                style="border-top: 1px solid #eeeeee; min-height: 55px"
                            >
                                <v-slide-x-transition>
                                    <v-checkbox
                                        v-if="
                                            isExcursion &&
                                            (!selectedGuests[0] || selectedGuests[0].orderId === guest.orderId)
                                        "
                                        v-model="selectedGuests"
                                        density="compact"
                                        hide-details
                                        class="me-2 mt-n1 flex-shrink-0"
                                        :value="guest"
                                    />
                                </v-slide-x-transition>
                                <div class="d-flex flex-column py-3 flex-shrink-0">
                                    <span class="font-weight-medium text-no-wrap">
                                        {{ `${guest.firstName} ${guest.lastName}` }}
                                    </span>
                                    <div class="d-flex">
                                        <span
                                            v-if="guest.departure.pickUpSign"
                                            style="white-space: nowrap"
                                            class="text-caption text--secondary font-weight-medium"
                                        >
                                            {{ `${$t('transfers.pickup_sign')}: ${guest.departure.pickUpSign}` }}
                                        </span>
                                    </div>
                                    <span
                                        v-if="!guest.adult"
                                        style="white-space: nowrap"
                                        class="text-caption text--secondary"
                                    >
                                        {{ `${$t('age')}: ${guest.age}` }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:item.seatNumber="{item}">
                    <div class="d-flex flex-column fill-height">
                        <div class="flex-grow-1 d-flex align-center pl-4" style="min-height: 55px">
                            <DetailsRow
                                v-model="item.seatNumber"
                                title="seat"
                                subtitle="no_seat"
                                @save-details="saveSeat(item)"
                            />
                        </div>
                        <div
                            v-for="guest in item.extraGuests"
                            :key="guest.id"
                            class="flex-grow-1 d-flex align-center pl-4"
                            style="border-top: 1px solid #eeeeee; min-height: 55px"
                        >
                            <DetailsRow
                                v-model="guest.seatNumber"
                                title="seat"
                                subtitle="no_seat"
                                @save-details="saveSeat(guest)"
                            />
                        </div>
                    </div>
                </template>
                <template v-slot:item.orderId="{item}">
                    <span
                        class="text-primary text-decoration-underline cursor-pointer"
                        @click="showPreviewModal(item.orderId)"
                    >
                        {{ item.orderId }}
                    </span>
                </template>
                <template v-slot:item.arrived="{item}">
                    <v-switch
                        v-model="item.arrivedAll"
                        class="mt-n1"
                        hide-details
                        density="compact"
                        color="primary"
                        @update:model-value="setArrived(item, $event)"
                    />
                </template>
                <template v-slot:item.orderStatus="{item}">
                    <span :class="item.statusColor">
                        {{ $t(`bookingStatus.${item.orderStatus.toUpperCase()}`) }}
                    </span>
                </template>
                <template v-slot:item.accommodation="{item}">
                    <div v-if="item.accommodation" class="d-flex flex-column py-3">
                        <div class="d-flex font-weight-medium justify-end justify-md-start">
                            <v-icon size="small" class="me-1 ms-n1 d-inline-block">{{ 'mdi-office-building' }}</v-icon>
                            <span v-text="item.accommodation.hotelName" />
                        </div>
                        <div>
                            {{ `${item.accommodation.counrtyName}, ${item.accommodation.cityName}` }}
                        </div>
                        <div class="text--secondary">
                            {{
                                `${getDateFormatted(item.accommodation.startDateTime)} - ${getDateFormatted(
                                    item.accommodation.endDateTime
                                )}`
                            }}
                        </div>
                    </div>
                </template>
                <template v-slot:item.departure="{item}">
                    <div v-if="item.departure" class="d-flex flex-column py-3">
                        <div>
                            {{ `${item.departure.place}` }}
                        </div>
                    </div>
                </template>
                <template v-slot:item.arrival="{item}">
                    <div v-if="item.arrival" class="d-flex flex-column py-3">
                        <div>
                            {{ `${item.arrival.place}` }}
                        </div>
                    </div>
                </template>
                <template v-slot:item.pricePrintable="{item}">
                    <div class="d-flex flex-column">
                        <span> {{ `${item.pricePrintable}` }} </span>
                        <payment-status-label :status="item.paymentStatus" />
                    </div>
                </template>
                <template v-slot:item.phoneNumber="{item}">
                    <span v-if="item.phoneNumber" class="py-3"> {{ `${item.phoneNumber}` }} </span>
                </template>
            </v-data-table>
        </v-expand-transition>
        <v-dialog v-model="reservationModal" max-width="1200">
            <v-card>
                <v-card-text class="px-0">
                    <scheduler-reservation-preview
                        v-if="reservationModal"
                        :table-columns="tableColumns"
                        :selected-order-id="selectedOrderId"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn @click="reservationModal = false">{{ $t('cancel') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-expand-transition>
            <div class="d-flex">
                <v-spacer v-if="isMobile" />
                <v-btn
                    v-if="selectedGuests[0] && isExcursion"
                    class="my-4"
                    :color="'primary'"
                    @click="showCalendarModal"
                >
                    {{ $t('up_sell') }}
                </v-btn>
            </div>
        </v-expand-transition>
        <activity-calendar-modal
            v-model="isShowCalendarModal"
            :city="city"
            :query-params="activityCalendarQueryParams"
            :first-start-date="selectedGroup.date"
            @close="clearSelectedOrderId"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue} from 'vue-facing-decorator'
    import GroupDetailsMobileDialog from '@/components/scheduler/modals/GroupDetailsMobileDialog.vue'
    import PaymentStatusLabel from '~src/components/account/snippets/paymentStatusLabel.src'
    import {adult, child} from '~src/utils/tourist-blank-states'
    import SchedulerReservationPreview from '@/components/scheduler/SchedulerReservationPreview.vue'
    import {downloadData} from '~/utils/helpers'
    import DetailsRow from '~/components/scheduler/DetailsRow.vue'

    @Component({
        components: {
            DetailsRow,
            PaymentStatusLabel,
            ActivityCalendarModal: defineAsyncComponent(
                () => import('@/components/activities/modals/ActivityCalendarModal.vue')
            ),
            GroupDetailsMobileDialog,
            SchedulerReservationPreview,
        },
    })
    export class GroupDetailsTable extends Vue {
        @VModel() groupDetails
        @Prop() selectedGroup
        @Prop() city
        @Prop({default: false, type: Boolean}) isShowSeats

        selectedGuests = []
        isShowCalendarModal = false

        reservationModal = false
        selectedOrderId = null

        async setArrived({serviceId}, event) {
            try {
                await this.$api.schedulerGroupPresence.post({
                    serviceId,
                    groupId: this.selectedGroup.groupId,
                    arrived: event,
                })
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async downloadExcel() {
            this.loading = true
            try {
                const data = await this.$api.downloadSchedulerGroupsDetails.get({
                    fileType: 'EXCEL',
                    groupId: this.selectedGroup.groupId,
                })

                await downloadData(data, 'schedulerGroupsDetails.xlsx')
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            }
            this.loading = false
        }

        getDateFormatted(dateStr) {
            return this.$dateFns.format(new Date(dateStr), 'dd MMM yyyy')
        }

        showCalendarModal() {
            const tourists = this.selectedGuests.map(e => {
                return e.adult
                    ? {
                          ...adult(),
                          firstName: e.firstName,
                          lastName: e.lastName,
                          age: e.age,
                      }
                    : {
                          ...child(),
                          firstName: e.firstName,
                          lastName: e.lastName,
                          age: e.age,
                      }
            })

            persistentStore.SET_TOURISTS(tourists)
            persistentStore.SET_SELECTED_ORDER_ID(this.selectedGuests[0].orderId)
            this.isShowCalendarModal = true
        }

        clearSelectedOrderId() {
            persistentStore.SET_SELECTED_ORDER_ID(null)
        }

        showPreviewModal(id) {
            this.selectedOrderId = id
            this.reservationModal = true
        }

        get activityCalendarQueryParams() {
            return {
                adults: this.selectedGuestsAdults,
                childrenAges: this.selectedGuestsChildrenAges,
            }
        }

        get selectedGuestsAdults() {
            return this.selectedGuests.reduce((adults, guest) => {
                return guest.adult ? adults + 1 : adults
            }, 0)
        }

        get selectedGuestsChildrenAges() {
            return this.selectedGuests.reduce((childrenAges, guest) => {
                if (!guest.adult) {
                    childrenAges.push(guest.age)
                }

                return childrenAges
            }, [])
        }

        get headers() {
            const headers = []

            const addHeaderIfValueExists = options => {
                if (this.guestsPrepared?.findIndex(e => !!e[options.value]) !== -1) {
                    headers.push(options)
                }
            }

            if (this.isMobile) {
                headers.push({title: this.$t('name'), value: 'name', minWidth: 200})
                addHeaderIfValueExists({title: this.$t('order'), value: 'orderId'})
                headers.push({title: this.$t('arrived'), value: 'arrived'})
                addHeaderIfValueExists({title: 'Status', value: 'orderStatus'})
            } else {
                headers.push({title: this.$t('name'), value: 'name', minWidth: 200})
                if (this.isShowSeats) {
                    headers.push({title: this.$t('seat'), value: 'seatNumber', minWidth: 200})
                }
                addHeaderIfValueExists({title: this.$t('order'), value: 'orderId'})
                headers.push({title: this.$t('arrived'), value: 'arrived'})

                if (this.isTransfer) {
                    addHeaderIfValueExists({title: this.$t('transfers.from'), value: 'departure', minWidth: 200})
                    addHeaderIfValueExists({title: this.$t('transfers.to'), value: 'arrival', minWidth: 200})
                }

                addHeaderIfValueExists({title: this.$t('status'), value: 'orderStatus'})
                addHeaderIfValueExists({
                    title: this.$t('serviceType.ACCOMMODATION'),
                    value: 'accommodation',
                    minWidth: 220,
                })
                addHeaderIfValueExists({title: this.$t('contacts'), value: 'phoneNumber', align: 'center'})
                if (this.personRole !== 'dispatcher') {
                    addHeaderIfValueExists({title: this.$t('price'), value: 'pricePrintable', align: 'center'})
                }
            }

            return headers
        }

        get tableColumns() {
            const tableColumns = [
                {title: this.$t('status'), value: 'status'},
                {title: this.$t('order_service'), value: 'title'},
                {title: this.$t('dates'), value: 'date', align: 'center'},
                {title: this.$t('order_travelers'), value: 'travellers'},
            ]
            if (this.personRole !== 'dispatcher') {
                tableColumns.push({title: this.$t('netto'), value: 'supplierPrice', align: 'right'})
                tableColumns.push({title: this.$t('brutto'), value: 'price', align: 'right'})
            }

            return tableColumns
        }

        get guestsPrepared() {
            return this.groupDetails?.guests
                .reduce((arr, guest) => {
                    const guestOrderExists = arr.find(e => e.orderId === guest.orderId)

                    if (!guestOrderExists) {
                        const preparedGuest = {
                            ...guest,
                            extraGuests: [],
                            arrivedAll: guest.arrived,
                        }

                        arr.push(preparedGuest)
                    } else {
                        guestOrderExists.extraGuests.push(guest)

                        if (!guest.arrived) {
                            guestOrderExists.arrivedAll = false
                        }
                    }

                    return arr
                }, [])
                .map(e => {
                    return {
                        ...e,
                        pricePrintable: e.price.printableValue,
                        statusColor: runtimeStore.orderServiceStatusColor(e.orderStatus),
                    }
                })
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isTransfer() {
            return this.groupDetails?.serviceType === 'TRANSFER'
        }

        get isExcursion() {
            return this.groupDetails?.serviceType === 'EXCURSION'
        }

        get personRole() {
            return authStore.personRole
        }

        get isTO1() {
            return authStore.isTO1
        }

        async saveSeat(item) {
            const {travelerRPH, seatNumber, serviceProcessId} = item
            this.groupDetails.guests.find(({id}) => id === item.id).seatNumber = item.seatNumber
            try {
                await this.$api.seatsByProcessId.put({travelerRPH, seatNumber, serviceProcessId})
                this.$toast.success(this.$t('success_message.saved_successfully'))
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            }
        }
    }

    export default GroupDetailsTable
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    .table-with-seat {
        :deep(div) {
            th:nth-of-type(1),
            td:nth-of-type(1) {
                padding-right: 0;
            }
            td:nth-of-type(2) {
                padding-left: 0;
            }
        }
    }
</style>
